import React, { useState, useEffect, useCallback } from 'react';
import SEO from '../components/SEO';
import axios from 'axios';
import { MapPin, Zap, Calendar, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/projects`);
      setProjects(data);
    } catch (error) {
      console.error('Projects fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const getImageSrc = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1713544123580-12096cc9eb12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwc29sYXIlMjBwYW5lbHMlMjByb29mfGVufDB8fHx8MTc3NjI2MTI4MXww&ixlib=rb-4.1.0&q=85';
    if (url.startsWith('/api')) return `${API_URL}${url}`;
    return url;
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
    if (url.endsWith('.mp4') || url.includes('.mp4')) return url;
    return null;
  };

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(p => p.status.toLowerCase() === filter.toLowerCase());

  return (
    <div>
      <SEO
        title="Running Projects - RESCO Invictus Green Energy Solutions"
        description="Explore our portfolio of completed and ongoing C&I solar power plant projects across India."
        keywords="solar projects, C&I solar installations, solar power plants, completed projects"
        url="/projects"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-surface" data-testid="projects-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-4 block" data-testid="projects-overline">
              Our Portfolio
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-primary mb-6" data-testid="projects-title">
              Running Projects
            </h1>
            <p className="text-base leading-relaxed text-text_muted" data-testid="projects-description">
              Explore our diverse portfolio of solar power projects. From large-scale industrial installations to commercial rooftop systems, we deliver excellence across all project sizes.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-white border-b border-border" data-testid="projects-filter-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-wrap gap-4 justify-center">
            {['all', 'completed', 'ongoing', 'upcoming'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-6 py-2 rounded font-medium transition-all ${
                  filter === status
                    ? 'bg-accent text-white'
                    : 'bg-surface text-text_main hover:bg-accent/10'
                }`}
                data-testid={`filter-${status}`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20" data-testid="projects-grid-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          {loading ? (
            <div className="text-center py-20" data-testid="projects-loading">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-text_muted">Loading projects...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-20" data-testid="projects-empty">
              <p className="text-xl text-text_muted">No projects found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  className="bg-white rounded-sm border border-border overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all"
                  data-testid={`project-card-${index}`}
                >
                  <div className="h-48 overflow-hidden">
                    {project.video_url && getYouTubeEmbedUrl(project.video_url) ? (
                      getYouTubeEmbedUrl(project.video_url).includes('youtube.com') ? (
                        <iframe
                          src={getYouTubeEmbedUrl(project.video_url)}
                          title={project.name}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          data-testid={`project-video-${index}`}
                        />
                      ) : (
                        <video
                          src={getYouTubeEmbedUrl(project.video_url)}
                          className="w-full h-full object-cover"
                          controls
                          data-testid={`project-video-${index}`}
                        />
                      )
                    ) : (
                      <img
                        src={getImageSrc(project.image_url)}
                        alt={project.name}
                        className="w-full h-full object-cover"
                        data-testid={`project-image-${index}`}
                      />
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className={`px-3 py-1 rounded text-xs font-semibold ${
                          project.status.toLowerCase() === 'completed'
                            ? 'bg-green-100 text-green-700'
                            : project.status.toLowerCase() === 'ongoing'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                        data-testid={`project-status-${index}`}
                      >
                        {project.status}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-primary mb-3" data-testid={`project-name-${index}`}>
                      {project.name}
                    </h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-text_muted">
                        <Building2 size={16} className="mr-2 text-accent" />
                        <span data-testid={`project-client-${index}`}>{project.client_name}</span>
                      </div>
                      <div className="flex items-center text-sm text-text_muted">
                        <MapPin size={16} className="mr-2 text-accent" />
                        <span data-testid={`project-location-${index}`}>{project.location}</span>
                      </div>
                      <div className="flex items-center text-sm text-text_muted">
                        <Zap size={16} className="mr-2 text-accent" />
                        <span data-testid={`project-capacity-${index}`}>{project.capacity}</span>
                      </div>
                      {project.completion_date && (
                        <div className="flex items-center text-sm text-text_muted">
                          <Calendar size={16} className="mr-2 text-accent" />
                          <span data-testid={`project-date-${index}`}>{project.completion_date}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-text_muted leading-relaxed" data-testid={`project-description-${index}`}>
                      {project.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Projects;
